import get from 'lodash/get';
import includes from 'lodash/includes';
import moment from 'moment';
import {getWindow} from 'ssr-window';
import APIUtils from '@spothero/utils/api';
import FormatUtils from 'utils/format';
import StorageUtils from '@spothero/utils/storage';
import Config from 'config';
import UserUtils from 'user/user-utils';
import SearchTracking from 'utils/search-tracking';
import SegmentUtils from 'utils/segment';
import CheckoutUtils from 'checkout/checkout-utils';

const window = getWindow();

const getErrorsFromResponse = errorResponse => {
    // error object is different for <500 and >=500 status codes
    const data =
        get(errorResponse, 'data.data') ||
        get(errorResponse, 'response.data.data');
    let errors = [];
    let extra = false;

    if (data) {
        errors = data.errors;
        extra = data.extra;
    }

    if (!errors.length || !errors[0].messages.length) {
        errors = [
            {
                messages: [
                    'An error occurred while reserving your parking spot. Please refresh the page and try again.',
                ],
            },
        ];
    }

    return {
        errors,
        extra,
    };
};

const ReservationAPI = {
    async add({
        checkoutData,
        selectedRate,
        monthlyStarts = null,
        facility,
        stripeToken = false,
        stripePaymentType = 'cc',
        setNewCardAsDefault = false,
        includeVehicleInResponse = false,
        includeFacilityInResponse = false,
        source = null,
        rentalSourceReferrer = null,
        checkoutStartTime = null,
        onSuccess,
        onError,
    }) {
        const {
            currencyType,
            user,
            promoCode,
            isMonthly,
            eventId,
            paymentRequired,
            purchaseForCustomer,
            purchaseForCustomerDescription,
            purchaseOnBehalfOfCustomer,
            initialReservationId,
            affiliate,
            selectedCreditCard,
            licensePlateRequired,
            selectedLicensePlate,
            licensePlateState,
            newLicensePlate,
            newLicensePlateName,
            newLicensePlateIsDefault,
            referralSource,
            smsParkingPass,
            forceRedundantMonthly,
            vehicleInfoId,
            vehicleProfileId,
            vehicleMake,
            vehicleModel,
            vehicleColor,
            unlistedModel,
            saveVehicleAsDefault,
            acceptedTerms,
            contractFullName,
            paymentOverlay,
        } = checkoutData;
        const ratePrice = !isMonthly
            ? get(selectedRate, 'price_breakdown.total_price') / 100
            : selectedRate.price / 100;
        const phoneNumberCleaned = user.phoneNumber
            ? user.phoneNumber.replace(/\D/g, '')
            : null;
        const isUserSavingNewCard =
            user.status === UserUtils.AUTH_STATE.USER &&
            selectedCreditCard === 'new' &&
            !purchaseForCustomer &&
            paymentRequired;
        const isMonthlyRecurringNewCard =
            selectedCreditCard === 'new' &&
            isMonthly &&
            selectedRate.recurrable;
        const isPaymentRequired =
            selectedCreditCard === 'admin' ? false : paymentRequired;
        const {searchUUID, actionUUID} = SearchTracking.getValues();
        /* eslint-disable camelcase */
        const postData = {
            currency_type:
                currencyType.toLowerCase() ||
                UserUtils.CURRENCY_TYPES.DEFAULT.toLowerCase(),
            email: user.email,
            price: parseInt(Math.round(Number(ratePrice) * 100), 10),
            facility: facility.parking_spot_id,
            apply_sh_credit: user.status === UserUtils.AUTH_STATE.USER,
            search_id: searchUUID,
            action_id: actionUUID,
            is_admin: user.isAdmin,
        };
        const config = {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        };
        const shExperimentVariations = StorageUtils.get(
            'sh-experiment-variations',
            'cookie'
        );
        let finalStripeToken = stripeToken;

        if (paymentOverlay) {
            // purchase was made using the Apple/Google Pay payment overlay
            finalStripeToken = paymentOverlay.token.id;
        }

        if (includeFacilityInResponse) {
            postData.include = 'facility';
        }

        if (includeVehicleInResponse) {
            if (postData.include) {
                postData.include += ',vehicle_info';
            } else {
                postData.include = 'vehicle_info';
            }
        }

        // start/end times
        if (isMonthly) {
            postData.monthly_start_date = moment(monthlyStarts).format(
                Config.apiDateFormat
            );
            postData.rule = selectedRate.fullRule;
        } else {
            postData.starts = selectedRate.starts;
            postData.ends = selectedRate.ends;
            postData.rule_group_id = selectedRate.rule_group_id;
        }

        // event id
        if (eventId) {
            postData.event = eventId;
        }

        // phone number
        if (user.phoneNumber && !phoneNumberCleaned) {
            postData.phone_number = user.phoneNumber;
        } else {
            if (phoneNumberCleaned) {
                postData.phone_number = phoneNumberCleaned;
            }
        }

        // license plate
        if (licensePlateRequired || !user.id) {
            postData.license_plate_str = selectedLicensePlate;

            // unknown license plate flag
            if (selectedLicensePlate === 'DONTKNOW') {
                postData.license_plate_unknown = true;
            }

            if (licensePlateState) {
                postData.license_plate_state = licensePlateState;
            }

            // new license plate
            if (newLicensePlate) {
                postData.license_plate_is_default = Boolean(
                    newLicensePlateIsDefault
                );

                if (newLicensePlateName) {
                    postData.license_plate_name = newLicensePlateName;
                }

                if (!purchaseForCustomer) {
                    postData.save_license_plate = true;
                }
            }
        }

        // promo code
        if (promoCode) {
            postData.promo_code = promoCode.code;
        }

        // payment data
        if (finalStripeToken) {
            postData.stripe_token = finalStripeToken;

            if (stripePaymentType && stripePaymentType !== 'cc') {
                postData.stripe_card_type = stripePaymentType;

                // Android/Google Pay requires these two fields on the BE for whatever reason...
                if (
                    stripePaymentType ===
                    CheckoutUtils.PAYMENT_REQUEST_TYPES.GOOGLE_PAY
                ) {
                    postData.google_instant_user_email = user.email;
                    postData.google_instant_card_info = 'Google Pay Card';
                }
            } else {
                postData.default_card =
                    user.status === UserUtils.AUTH_STATE.USER &&
                    user.justCreated
                        ? true
                        : setNewCardAsDefault;
            }
        } else {
            if (isPaymentRequired) {
                postData.card = selectedCreditCard;
            }
        }

        // save new card
        if (isUserSavingNewCard || isMonthlyRecurringNewCard) {
            postData.save_card = true;
        }

        // purchase for customer
        if (purchaseForCustomer) {
            postData.purchase_for_customer = true;
            postData.purchase_for_customer_description = purchaseForCustomerDescription;

            if (initialReservationId) {
                postData.referenced_rental_id = initialReservationId;
            }
        }

        // purchase on behalf of customer
        if (purchaseOnBehalfOfCustomer) {
            postData.purchase_on_behalf_of_customer = true;
        }

        if (affiliate) {
            postData.affiliate = affiliate;
        }

        if (referralSource) {
            postData.referral_source = referralSource;
        }

        // SMS Parking Pass
        if (smsParkingPass && phoneNumberCleaned) {
            postData.sms_parking_pass = true;
        }

        // vehicle data
        if (vehicleProfileId) {
            postData.vehicle_profile_id = vehicleProfileId;
        } else if (vehicleInfoId) {
            postData.vehicle_info_id = vehicleInfoId;

            if (vehicleMake && vehicleModel) {
                postData.vehicle_make = vehicleMake;
                postData.vehicle_model = vehicleModel;
                postData.set_vehicle_default = Boolean(saveVehicleAsDefault);
            }
        } else if (unlistedModel) {
            postData.unlisted_model = unlistedModel;
        }

        if (vehicleColor) {
            postData.vehicle_color = vehicleColor;
        }

        if (acceptedTerms) {
            postData.accept_terms_and_conditions = true;
        }

        if (contractFullName) {
            postData.contract_full_name = contractFullName;
        }

        // Force redundant non-recurring monthly reservation?
        if (forceRedundantMonthly) {
            postData.force_redundant_monthly = true;
        }

        if (source) {
            postData.rental_source_title = source;
        }

        if (rentalSourceReferrer) {
            postData.rental_source_referrer = rentalSourceReferrer;
        } else {
            const referrerCookie = StorageUtils.get('sh-referrer', 'cookie');

            if (referrerCookie) {
                postData.rental_source_referrer = referrerCookie;
            }
        }

        if (shExperimentVariations) {
            config.headers = {
                ...config.headers,
                'x-spothero-experiments': JSON.stringify(
                    shExperimentVariations
                ),
            };
        }

        postData.mixpanel_id = SegmentUtils.getAnonymousId();

        try {
            const {
                data: {
                    data: {reservation},
                },
            } = await APIUtils.post('reservations/reserve/', postData, config);
            const {
                reservation_id: rentalId,
                access_key: accessKey,
                reservation_type: reservationType,
            } = reservation;
            const path =
                reservationType === 'monthly'
                    ? '/successful-monthly-payment'
                    : '/successful-payment';

            if (onSuccess) {
                onSuccess(checkoutData, reservation);
            }

            // remove any existing referrer cookies on purchase
            StorageUtils.remove('sh-referrer', 'cookie');

            // hide the Apple/Google Pay payment overlay
            if (paymentOverlay) {
                paymentOverlay.complete('success');
            }

            if (!(source && includes(source, 'widget'))) {
                window.location = `${path}?initial_purchase=true&rentalid=${rentalId}&key=${accessKey}`;
            }
        } catch (error) {
            const {errors, extra} = getErrorsFromResponse(error);

            // show error in the Apple/Google Pay payment overlay so that it doesn't look like it keeps trying to process
            if (paymentOverlay) {
                paymentOverlay.complete('fail');
            }

            onError(errors, extra);
        }
        /* eslint-enable camelcase */
    },

    async rebook({
        checkoutData,
        selectedRate,
        facility,
        rebookReservationId,
        onError,
    }) {
        const {
            currencyType,
            isMonthly,
            licensePlateRequired,
            licensePlateState,
            newLicensePlateIsDefault,
            saveVehicleAsDefault,
            selectedLicensePlate,
            user,
            vehicleColor,
            vehicleInfoId,
            vehicleProfileId,
            vehicleMake,
            vehicleModel,
            vehicleSelectPresent,
            supportsOversizeFeeType,
            purchaseForCustomerDescription,
        } = checkoutData;
        /* eslint-disable camelcase */
        const price = !isMonthly
            ? get(selectedRate, 'price_breakdown.total_price')
            : selectedRate.price;
        const postData = {
            currency_type: currencyType.toLowerCase(),
            email: user.email,
            ends: selectedRate.ends,
            original_rental_id: rebookReservationId,
            price,
            rebook_facility_id: facility.parking_spot_id,
            rule_group_id: selectedRate.rule_group_id,
            spothero_user_id: user.id,
            starts: selectedRate.starts,
            ...((vehicleSelectPresent || supportsOversizeFeeType) && {
                vehicle_info_id: vehicleInfoId,
                vehicle_profile_id: vehicleProfileId,
                vehicle_make: vehicleMake,
                vehicle_model: vehicleModel,
                vehicle_color: vehicleColor,
                set_vehicle_default: Boolean(saveVehicleAsDefault),
            }),
            ...(user.phoneNumber && {
                phone_number: user.phoneNumber,
            }),
            ...(licensePlateRequired &&
                selectedLicensePlate !== 'DONTKNOW' && {
                    license_plate_str: selectedLicensePlate,
                    license_plate_state: licensePlateState,
                    license_plate_is_default: newLicensePlateIsDefault,
                }),
            rebook_reason: purchaseForCustomerDescription,
        };
        /* eslint-enable camelcase */
        const config = {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        };

        try {
            const {
                data: {
                    data: {
                        refund_amount: refundAmount,
                        new_reservation_id: rentalId,
                    },
                },
            } = await APIUtils.post('hero/rebook/', postData, config);

            if (refundAmount > 0) {
                const currencySymbol = FormatUtils.currencySymbol(
                    selectedRate.currency_type
                );

                // eslint-disable-next-line no-alert
                alert(
                    `Please refund ${currencySymbol}${refundAmount} to the customer via stripe.`
                );
            }

            window.location = `/successful-payment?rentalid=${rentalId}`;
        } catch (error) {
            const {errors, extra} = getErrorsFromResponse(error);

            onError(errors, extra);
        }
    },
};

export default ReservationAPI;
